import React, { BaseSyntheticEvent, useEffect, useRef, useState } from 'react';
import { Button, Popover, Select } from 'antd';
import { Canvas, IText } from 'fabric/fabric-impl';
import { CloseOutlined, PlusCircleFilled } from '@ant-design/icons';

import styles from './TextFormatControls.module.less';
import ButtonTool from '../UI/ButtonTool';
import ShowIf from '../UI/ShowIf';
import { EditorStore, TextFormatSettings, useStore } from '../../store/createStore';

import bold from '../../assets/images/icons/BOLD_unpressed.svg';
import boldPressed from '../../assets/images/icons/BOLD_pressed.svg';
import italic from '../../assets/images/icons/ITALIC_unpressed.svg';
import italicPressed from '../../assets/images/icons/ITALIC_pressed.svg';
import alignLeft from '../../assets/images/icons/ALIGN_LEFT_unpressed.svg';
import alignLeftPressed from '../../assets/images/icons/ALIGN_LEFT_pressed.svg';
import alignCenter from '../../assets/images/icons/ALIGN_CENTER_unpressed.svg';
import alignCenterPressed from '../../assets/images/icons/ALIGN_CENTER_pressed.svg';
import { ReactComponent as ColorSVG } from '../../assets/images/icons/COLOR.svg';
import { ReactComponent as ColorSVGPressed } from '../../assets/images/icons/COLOR_PRESSED.svg';
import { ReactComponent as ColorSVGSelected } from '../../assets/images/icons/COLOR_SELECTED.svg';
import { CanvasHandler } from '../../handlers/CanvasHandler';
import { isMobile } from 'react-device-detect';

const { Option } = Select;

function TextFormatControls() {
  const [textFormatSettings, setTextFormatSettings] = useStore<EditorStore, TextFormatSettings>(
    'textFormat'
  );
  const activeObjectIText = useStore<EditorStore, IText>('activeObjectIText')[0];
  const canvas = useStore<EditorStore, Canvas>('canvas')[0];
  const [isActiveColorSelection, setIsActiveColorSelection] = useState(false);
  const selectFontRef = useRef<HTMLSelectElement>(null);
  const canvasHandler = new CanvasHandler(canvas);

  useEffect(() => {
    // @ts-ignore
    selectFontRef.current.value = textFormatSettings.fontFamily;
  }, [textFormatSettings, activeObjectIText]);

  const handleSelectFont = (value: string) => {
    setTextFormatSettings((textFormat: TextFormatSettings) => ({
      ...textFormat,
      fontFamily: value,
    }));

    if (activeObjectIText) {
      // Si existe un objeto de tipo texto interactivo activo, cambia su fuente a la última seleccionada
      activeObjectIText.fontFamily = value;
      activeObjectIText.hiddenTextarea?.focus();
      canvas.requestRenderAll();
    }
  };

  const isActiveItalic = () => {
    return textFormatSettings.fontStyle === 'italic';
  };

  const isActiveAlign = (align: string) => {
    return textFormatSettings.textAlign === align;
  };

  const isActiveColor = (color: string) => {
    return textFormatSettings.color === color;
  };

  const selectIconColorSVG = (iconProps: any, color: string) => {
    if (isActiveColor(color)) {
      return <ColorSVGSelected {...iconProps} fill={color} />;
    }
    return <ColorSVG {...iconProps} fill={color} />;
  };

  const handleSelectBold = () => {
    const isActiveBold = !textFormatSettings.bold;
    const fontWeight = isActiveBold ? 'bold' : void 0;
    setTextFormatSettings((textFormat: TextFormatSettings) => ({
      ...textFormat,
      bold: isActiveBold,
    }));

    if (activeObjectIText) {
      // Si existe un objeto de tipo texto interactivo activo, cambia su fontWeight al último seleccionado
      activeObjectIText.fontWeight = fontWeight;
      activeObjectIText.hiddenTextarea?.focus();
      canvas.requestRenderAll();
    }
  };

  const handleSelectItalicStyle = () => {
    const fontStyle = !isActiveItalic() ? 'italic' : 'normal';
    setTextFormatSettings((textFormat: TextFormatSettings) => ({
      ...textFormat,
      fontStyle: fontStyle,
    }));

    if (activeObjectIText) {
      // Si existe un objeto de tipo texto interactivo activo, cambia su fontStyle al último seleccionado
      activeObjectIText.fontStyle = fontStyle;
      activeObjectIText.hiddenTextarea?.focus();
      canvas.requestRenderAll();
    }
  };

  const handleSelectAlign = (align: string) => {
    setTextFormatSettings((textFormat: TextFormatSettings) => ({
      ...textFormat,
      textAlign: align,
    }));

    if (activeObjectIText) {
      // Si existe un objeto de tipo texto interactivo activo, cambia su fontStyle al último seleccionado
      activeObjectIText.textAlign = align;
      activeObjectIText.hiddenTextarea?.focus();
      canvas.requestRenderAll();
    }
  };

  const handleSelectColor = (color: string) => {
    setTextFormatSettings((textFormat: TextFormatSettings) => ({
      ...textFormat,
      color: color,
    }));

    if (activeObjectIText) {
      // Si existe un objeto de tipo texto interactivo activo, cambia su fontStyle al último seleccionado
      activeObjectIText.set('fill', color);
      activeObjectIText.hiddenTextarea?.focus();
      canvas.requestRenderAll();
    }
  };

  const handleAddNewText = () => {
    canvasHandler.addTextElem(textFormatSettings);
  };

  const popoverColor = (
    <div className="flex">
      <div className="inline-block md:flex md:justify-center overflow-x-scroll h-full w-full bg-primary disable-scrollbars">
        <div className="h-1/2 bg-primary px-4 overflow-x-hidden inline-flex gap-x-2">
          <ButtonTool
            className="w-10"
            icon={(props: any) => selectIconColorSVG(props, '#61C393')}
            isSelected={false}
            onClick={() => handleSelectColor('#61C393')}
          />

          <ButtonTool
            className="w-10"
            icon={(props: any) => selectIconColorSVG(props, '#3C3C3B')}
            isSelected={false}
            onClick={() => handleSelectColor('#3C3C3B')}
          />

          <ButtonTool
            className="w-10"
            icon={(props: any) => selectIconColorSVG(props, '#F0ECC9')}
            isSelected={false}
            onClick={() => handleSelectColor('#F0ECC9')}
          />

          <ButtonTool
            className="w-10"
            icon={(props: any) => selectIconColorSVG(props, '#E89E9E')}
            isSelected={false}
            onClick={() => handleSelectColor('#E89E9E')}
          />

          <ButtonTool
            className="w-10"
            icon={(props: any) => selectIconColorSVG(props, '#CD4747')}
            isSelected={false}
            onClick={() => handleSelectColor('#CD4747')}
          />

          <ButtonTool
            className="w-10"
            icon={(props: any) => selectIconColorSVG(props, '#FFBA67')}
            isSelected={false}
            onClick={() => handleSelectColor('#FFBA67')}
          />

          <ButtonTool
            className="w-10"
            icon={(props: any) => selectIconColorSVG(props, '#D1C775')}
            isSelected={false}
            onClick={() => handleSelectColor('#D1C775')}
          />

          <ButtonTool
            className="w-10"
            icon={(props: any) => selectIconColorSVG(props, '#3AB8EF')}
            isSelected={false}
            onClick={() => handleSelectColor('#3AB8EF')}
          />

          <ButtonTool
            className="w-10"
            icon={(props: any) => selectIconColorSVG(props, '#0070B8')}
            isSelected={false}
            onClick={() => handleSelectColor('#0070B8')}
          />

          <ButtonTool
            className="w-10"
            icon={(props: any) => selectIconColorSVG(props, '#6D5A82')}
            isSelected={false}
            onClick={() => handleSelectColor('#6D5A82')}
          />
        </div>
      </div>
      <div className="flex items-center mr-3 ml-3">
        <CloseOutlined
          className="cursor-pointer text-secondary"
          onClick={() => setIsActiveColorSelection(false)}
        />
      </div>
    </div>
  );

  return (
    <div className="flex flex-wrap justify-center items-center h-full">
      <div className="sm:w-7/12 md:w-1/2 lg:w-1/2 xl:w-1/3 w-full px-8 flex flex-wrap h-full">
        <div className="w-full flex">
          <Button
            onClick={handleAddNewText}
            type="primary"
            className={`${styles['add-text-button']}`}
            shape="circle"
            icon={<PlusCircleFilled />}
          />
        </div>
        <div className={`${styles['format-controls__item']} w-full flex h-1/3`}>
          <div
            className={`text-secondary ${styles['format-controls__label']} w-1/5 text-right mr-8 mt-auto`}
          >
            Fuente
          </div>
          <div className={`${styles['format-controls__control']} w-4/5 mb-0 mt-auto`}>
            <Select
              size="small"
              ref={selectFontRef}
              defaultValue="Akaya Kanadaka"
              className="w-full"
              onChange={handleSelectFont}
            >
              <Option value="Akaya Kanadaka">
                <span style={{ fontFamily: 'Akaya Kanadaka, serif' }}>Akaya Kanadaka</span>
              </Option>
              <Option value="Alumni Sans">
                <span style={{ fontFamily: 'Alumni Sans, serif' }}>Alumni Sans</span>
              </Option>
              <Option value="Averia Serif Libre">
                <span style={{ fontFamily: 'Averia Serif Libre, serif' }}>Averia Serif Libre</span>
              </Option>
              <Option value="Monoton">
                <span style={{ fontFamily: 'Monoton, serif' }}>Monoton</span>
              </Option>
              <Option value="Sacramento">
                <span style={{ fontFamily: 'Sacramento, serif' }}>Sacramento</span>
              </Option>
              <Option value="Special Elite">
                <span style={{ fontFamily: 'Special Elite, serif' }}>Special Elite</span>
              </Option>
              <Option value="Spectral">
                <span style={{ fontFamily: 'Spectral, serif' }}>Spectral</span>
              </Option>
              <Option value="STIX Two Text">
                <span style={{ fontFamily: 'STIX Two Text, serif' }}>STIX Two Text</span>
              </Option>
              <Option value="Tangerine">
                <span style={{ fontFamily: 'Tangerine, serif' }}>Tangerine</span>
              </Option>
              <Option value="UnifrakturCook">
                <span style={{ fontFamily: 'UnifrakturCook, serif' }}>UnifrakturCook</span>
              </Option>
            </Select>
          </div>
        </div>

        <div className={`${styles['format-controls__item']} w-full flex h-1/2`}>
          <div
            className={`text-secondary ${styles['format-controls__label']} w-1/5 text-right mr-8 my-auto`}
          >
            Estilo
          </div>
          <div className={`w-4/5 flex gap-x-3 justify-center`}>
            <ButtonTool
              icon={textFormatSettings.bold ? boldPressed : bold}
              isSelected={textFormatSettings.bold}
              onClick={handleSelectBold}
            />
            <ButtonTool
              icon={isActiveItalic() ? italicPressed : italic}
              isSelected={isActiveItalic()}
              onClick={handleSelectItalicStyle}
            />
            <ButtonTool
              icon={isActiveAlign('left') ? alignLeftPressed : alignLeft}
              isSelected={isActiveAlign('left')}
              onClick={() => handleSelectAlign('left')}
            />
            <ButtonTool
              icon={isActiveAlign('center') ? alignCenterPressed : alignCenter}
              isSelected={isActiveAlign('center')}
              onClick={() => handleSelectAlign('center')}
            />
            <Popover
              overlayClassName="popover-color"
              overlayStyle={{ maxWidth: window.innerWidth - window.innerWidth * 0.2 }}
              content={popoverColor}
              placement={isMobile ? 'bottomRight' : 'bottomRight'}
              visible={isActiveColorSelection}
            >
              <ButtonTool
                icon={(props: any) =>
                  isActiveColorSelection ? (
                    <ColorSVGPressed {...props} fill={textFormatSettings.color} />
                  ) : (
                    <ColorSVGSelected {...props} fill={textFormatSettings.color} />
                  )
                }
                isSelected={false}
                onClick={() => setIsActiveColorSelection((state) => !state)}
              />
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TextFormatControls;
